import React, { useState, useCallback, useMemo } from 'react'

import Modal from 'react-modal'

import {
  Accordion, Button, Close, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number } from 'prop-types'

function getFaqData(downloadLimit) {
  return [
  {
    id: 'faq-0',
    question: 'What is CGTrader Essentials?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
          CGTrader Essentials is a subscription service for individual customers or companies,
          allowing them to license 3D and 3D print models for a fixed monthly fee. The Essentials
          subscription is designed to capture an emerging market of individual 3D artists, game
          developers, and creators who need regular access to quality 3D assets.
        </p>
    ),
  },
  {
    id: 'faq-1',
    question: 'What type of customers will purchase CGTrader Essentials?',
    answer: (
      <>
        <p className="subscription-overview-modal__answer-paragraph">
          Our Essentials subscription provides the best value for individuals and small studios who
          need multiple 3D models per month for their projects. This includes:
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Game developers (indie and small studios)
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            3D artists and freelancers
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Architectural visualization professionals
          </li>
          <li className="subscription-overview-modal__answer-list-item">Product designers</li>
          <li className="subscription-overview-modal__answer-list-item">VR/AR content creators</li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          To ensure fair usage and prevent abuse of the platform, the subscription has a monthly
          download cap of {downloadLimit} models per month. CGTrader keeps the right to amend the cap.
        </p>
      </>
    ),
  },
  {
    id: 'faq-2',
    question: 'How can I join CGTrader Essentials?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        Participation in CGTrader Essentials is managed through the &apos;Maximise your
        earnings&apos; setting in your account. Once you enable this setting in your account,
        eligible entry-level, low-priced models from your portfolio will participate in the
        subscription.
      </p>
    ),
  },
  {
    id: 'faq-3',
    question: 'How will I get paid?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        Participating contributors are paid via the Subscriber Share revenue model.
      </p>
    ),
  },
  {
    id: 'faq-4',
    question: 'What is the Subscriber share revenue model?',
    answer: (
      <>
        <p className="subscription-overview-modal__answer-paragraph">
          Contributor earnings on CGTrader Essentials are based on a &quot;Subscriber Share&quot;
          revenue model:
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Subscription revenue net of payment fees is allocated on a monthly basis among the
            individual contributors whose models were downloaded by each subscriber during that
            month
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Your earnings payout will be calculated based on your subscription revenue and payout
            rate as established in Section 25.7 of the General Terms & Conditions and will include
            all taxes that shall be due in accordance with the applicable laws.
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Higher-quality models that subscribers choose to download more frequently will generate
            proportionally higher earnings
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>Example calculation:</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Subscriber #1 and subscriber #2 pays $9.99/month each
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            If subscriber #1 downloads 2 models that month, and 1 model is yours (50%), you would
            earn 50% x ($9.99 - payment fees) x payout rate
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            If subscriber #2 downloads 5 models that month, and 5 are yours (100%), you would earn
            100% x ($9.99 - payment fees) x payout rate
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>Withholding tax:</strong> Subscription earnings are subject to withholding tax calculated per
          seller on gross subscription earnings and deducted before payout. Applicable tax rates:
          15% for individuals, 10% for businesses, 0% for sellers with Lithuanian tax residence.
        </p>
        <p className="subscription-overview-modal__answer-paragraph">
          Deducted tax is remitted to Lithuanian tax authorities. Payout records show gross
          earnings, applied tax rate, withholding tax amount, and net payout, visible under My
          Sales &rarr; Payments section. Tax rate is determined by the legal status specified in
          the payment agreement at the time the payout is initiated.
        </p>
      </>
    ),
  },
  {
    id: 'faq-5',
    question: 'How will my earnings change? Will this cannibalize my regular sales?',
    answer: (
      <>
        <p className="subscription-overview-modal__answer-paragraph">
          We understand concerns about cannibalization, but the data shows that cannibalization
          risk is limited for several important reasons:
        </p>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>1. Different customer segments</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Subscription targets new customers who can&apos;t afford multiple individual purchases
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Subscription offers a complementary channel, not a replacement
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>2. Expanded market reach</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Lower barrier to entry attracts customers who previously used free alternatives or
            pirated content;
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Expansion of your audience, as users will be encouraged to follow and share your models;
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>3. Pricing strategy</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Individual model prices remain unchanged
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Enterprise customers prefer individual licenses for specific assets
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>4. Revenue diversification</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Earn from both channels simultaneously
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Multiple downloads from subscribers compound your earnings
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Popular models gain visibility, potentially driving individual sales
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>5. Proven model</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Similar platforms show subscriptions increase total contributor earnings
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Expands addressable market and creates recurring revenue
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>Expected Impact:</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Additional revenue stream from new customer segment
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Increased visibility and more stable income
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Higher total downloads across both channels
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          <strong>Example Scenario:</strong>
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Before: 10 sales/month at $5 = $35 net earnings
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            After: 8 sales ($28) + subscription downloads ($15) = $43/month (22% increase)
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq-6',
    question: 'How will I track my earnings?',
    answer: (
      <>
        <p className="subscription-overview-modal__answer-paragraph">
          Your CGTrader Essentials earnings can be found in your dashboard. There will be a
          dashboard in the Subscription area showcasing the earnings.
        </p>
        <p className="subscription-overview-modal__answer-paragraph">Your dashboard will show:</p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Number of downloads from Essentials subscribers per model
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Total earnings from Essentials subscriptions
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq-7',
    question: 'When are my earnings paid?',
    answer: (
      <>
        <p className="subscription-overview-modal__answer-paragraph">
          Earnings are automatically included in the monthly payment cycle for CGTrader
          contributors:
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            Earnings for the prior month are calculated at the beginning of every calendar month
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            The same payout method will be used as for regular payouts
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            Your earnings from subscription will be paid out as a separate payment
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'faq-8',
    question: 'Will my models still be available outside of CGTrader Essentials?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        Yes, your models will remain part of the main CGTrader marketplace and available for all
        customers to purchase individually at your set prices. Essentials subscription is an
        additional monetization option that runs parallel to traditional single-purchase licensing.
      </p>
    ),
  },
  {
    id: 'faq-9',
    question: 'Can I opt out?',
    answer: (
      <>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">
            You can opt out of CGTrader Essentials at any time
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            If you prefer not to participate, you can disable the setting at any time under:
            {' '}<strong>My Sales &rarr; Discounts &rarr; Maximise your earnings</strong>
          </li>
          <li className="subscription-overview-modal__answer-list-item">
            When you opt out, your eligible models are removed from CGTrader Essentials immediately.
          </li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          Note: It may take some time for search results and other pages across the platform to
          refresh. During this time, your models may still appear in some subscription search
          results, but subscription downloads will already follow your updated setting.
        </p>
      </>
    ),
  },
  {
    id: 'faq-10',
    question: 'What if I want to opt back in later?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        To opt back into CGTrader Essentials, go to <strong>My Sales &rarr; Discounts &rarr;
        Maximise your earnings</strong> and select opt-in again for the models. Your models will be available to
        Essentials subscribers soon.
      </p>
    ),
  },
  {
    id: 'faq-11',
    question: 'Can I remove select models from CGTrader Essentials?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        No, models are included in CGTrader Essentials at the account level.
      </p>
    ),
  },
  {
    id: 'faq-12',
    question: 'What types of models are eligible for CGTrader Essentials?',
    answer: (
      <>
        <p className="subscription-overview-modal__answer-paragraph">
          Most entry level 3D models in the marketplace are eligible, including:
        </p>
        <ul className="subscription-overview-modal__answer-list">
          <li className="subscription-overview-modal__answer-list-item">Game-ready models</li>
          <li className="subscription-overview-modal__answer-list-item">High-poly models</li>
          <li className="subscription-overview-modal__answer-list-item">Low-poly models</li>
          <li className="subscription-overview-modal__answer-list-item">PBR textured models</li>
          <li className="subscription-overview-modal__answer-list-item">
            Rigged and animated models
          </li>
          <li className="subscription-overview-modal__answer-list-item">3D printed models</li>
        </ul>
        <p className="subscription-overview-modal__answer-paragraph">
          High-end, premium models are excluded from the subscription. Models with special
          licensing restrictions (custom or editorial) or exclusive marketplace agreements are not
          eligible.
        </p>
      </>
    ),
  },
  {
    id: 'faq-13',
    question: 'Under which license will the models be available to subscribers?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        You will be able to opt-in with your eligible models published under &quot;Royalty
        free&quot; and &quot;Royalty free, No AI&quot; licenses. Regardless of the original
        license, models for subscribers will always be available under the <strong>&quot;Royalty
        free, No AI&quot;</strong> license.
      </p>
    ),
  },
  {
    id: 'faq-14',
    question: 'Will subscribers be able to use my models after expiration or termination of '
      + 'subscription?',
    answer: (
      <p className="subscription-overview-modal__answer-paragraph">
        Subscribers will be able to continue using your models on the projects completed during the
        active subscription period, but they will not be allowed to use your models in new projects
        after expiration or termination of the subscription.
      </p>
    ),
  },
]
}

function SubscriptionOverviewModal({ isOpen, onClose: handleClose, downloadLimit = 25 }) {
  const [accordionStates, setAccordionStates] = useState({})

  const faqData = useMemo(() => getFaqData(downloadLimit), [downloadLimit])

  const handleAccordionClick = useCallback((value, id) => {
    setAccordionStates(prevStates => ({
      ...prevStates,
      [id]: value,
    }))
  }, [])

  const faqItems = useMemo(
    () => faqData.map(({ id, question, answer }) => ({
      id,
      label: <span className="subscription-overview-modal__question">{question}</span>,
      content: <div className="subscription-overview-modal__answer">{answer}</div>,
      isOpen: accordionStates[id],
    })),
    [accordionStates, faqData],
  )

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick
      onRequestClose={handleClose}
      contentLabel="Subscription Overview"
      ariaHideApp={false}
      className={{
        base: '',
        afterOpen:
          'overlay-modal--container_after-open overlay-modal-container--subscription-overview',
        beforeClose: 'overlay-modal--container_before-close',
      }}
      overlayClassName={{
        base: 'overlay-modal--background',
        afterOpen: 'overlay-modal--background_after-open',
        beforeClose: 'overlay-modal--background_before-close',
      }}
    >
      <div className="subscription-overview-modal">
        <div className="subscription-overview-modal__header">
          <h2 className="subscription-overview-modal__title">Subscription Overview</h2>
          <div className="subscription-overview-modal__close-button-container">
            <Button
              size="small"
              type="ghost"
              variant="only-icon"
              customClass="subscription-overview-modal__close-button"
              onClick={handleClose}
              icon={<Icon svgComponent={Close} />}
            />
          </div>
        </div>
        <div className="subscription-overview-modal__content">
          <p className="subscription-overview-modal__subtitle">
            Answers to questions about CGTrader Essentials subscription and contributor earnings.
          </p>

          <h3 className="subscription-overview-modal__faqs-title">Overview</h3>
          <p>
            CGTrader is expanding your earnings opportunities by introducing a new
            {' '}
            &quot;CGTrader Essentials&quot; subscription option for our customers.
          </p>

          <h3 className="subscription-overview-modal__faqs-title">FAQs</h3>

          <div className="subscription-overview-modal__faq-accordion">
            <Accordion
              onClick={handleAccordionClick}
              items={faqItems}
            />
          </div>
        </div>
        <div className="subscription-overview-modal__footer">
          <h3 className="subscription-overview-modal__footer-title">Additional Support</h3>
          <p className="subscription-overview-modal__footer-text">
            Our goal is to build a sustainable, thriving marketplace where you can continue to
            succeed over the long term. Please contact us at{' '}
            <a href="mailto:subscriptions@cgtrader.com">subscriptions@cgtrader.com</a> with any
            additional questions or concerns about CGTrader Essentials.
          </p>
        </div>
      </div>
    </Modal>
  )
}

SubscriptionOverviewModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  downloadLimit: number,
}

export default React.memo(SubscriptionOverviewModal)
